// SPDX-FileCopyrightText: 2026 Stadtplatz.de
//
// SPDX-License-Identifier: EUPL-1.2

/**
 * Maps selected range to date_filter format for API calls
 * Used by: LLM Usage Reports
 *
 * @param {Object} selectedRange - The selected range object with label and days
 * @param {string} customStart - Custom start date (YYYY-MM-DD)
 * @param {string} customEnd - Custom end date (YYYY-MM-DD)
 * @returns {Object|null} Date filter object or null
 */
export const getDateFilter = (selectedRange, customStart, customEnd) => {
  if (selectedRange.label === "Custom Range" && customStart && customEnd) {
    return {
      date_filter: "custom",
      start_date: `${customStart}T00:00:00`,
      end_date: `${customEnd}T23:59:59`,
    };
  } else if (selectedRange.label === "Today") {
    return { date_filter: "today" };
  } else if (selectedRange.days) {
    return { date_filter: `${selectedRange.days}days` };
  }
  return null;
};

/**
 * Checks if custom range is selected and dates are complete
 * Used by: LLM Usage Reports, API Analytics
 *
 * @param {Object} selectedRange - The selected range object
 * @param {string} customStart - Custom start date
 * @param {string} customEnd - Custom end date
 * @returns {boolean} True if custom range is selected with complete dates
 */
export const isCustomRangeComplete = (
  selectedRange,
  customStart,
  customEnd,
) => {
  return selectedRange.label === "Custom Range" && customStart && customEnd;
};

/**
 * Checks if custom range is selected but dates are incomplete
 * Used to prevent API calls with incomplete data
 *
 * @param {Object} selectedRange - The selected range object
 * @param {string} customStart - Custom start date
 * @param {string} customEnd - Custom end date
 * @returns {boolean} True if custom range is selected but dates are incomplete
 */
export const isCustomRangeIncomplete = (
  selectedRange,
  customStart,
  customEnd,
) => {
  return selectedRange.label === "Custom Range" && (!customStart || !customEnd);
};

/**
 * Builds date parameters for API calls
 * Used by: API Analytics, Business Metrics Dashboard
 *
 * @param {Object} selectedRange - The selected range object
 * @param {string} customStart - Custom start date
 * @param {string} customEnd - Custom end date
 * @returns {Object} Parameters object with startDate/endDate or days
 */
export const buildDateParams = (selectedRange, customStart, customEnd) => {
  const params = {};

  if (selectedRange.label === "Custom Range" && customStart && customEnd) {
    params.startDate = customStart;
    params.endDate = customEnd;
  } else if (selectedRange.days !== null) {
    params.days = selectedRange.days;
  }

  return params;
};

/**
 * Resets custom date inputs when range changes
 *
 * @param {Object} newRange - The newly selected range
 * @param {Function} setCustomStart - State setter for custom start date
 * @param {Function} setCustomEnd - State setter for custom end date
 */
export const handleRangeChange = (newRange, setCustomStart, setCustomEnd) => {
  if (newRange.label !== "Custom Range") {
    setCustomStart("");
    setCustomEnd("");
  }
};
